"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.tutorialOpenApiDocs = exports.TutorialSchema = void 0;
const avatarActions_openapi_1 = require("./avatarActions.openapi");
const knowledgeActions_openapi_1 = require("./knowledgeActions.openapi");
const todoActions_openapi_1 = require("./todoActions.openapi");
const ErrorResponse = {
    type: 'object',
    properties: {
        success: {
            type: 'boolean',
            example: false
        },
        error: {
            type: 'string',
            description: 'Human-readable error message'
        },
        error_code: {
            type: 'integer',
            description: 'Numeric error code for programmatic handling'
        }
    }
};
const FileSchema = {
    type: 'object',
    description: 'File attachment metadata',
    properties: {
        id: {
            type: 'number',
            description: 'Unique file identifier (for existing files)'
        },
        filename: {
            type: 'string',
            description: 'System filename'
        },
        originalName: {
            type: 'string',
            description: 'Original filename with extension'
        },
        mimeType: {
            type: 'string',
            description: 'MIME type of the file',
            example: 'image/png'
        },
        size: {
            type: 'number',
            description: 'File size in bytes'
        },
        path: {
            type: 'string',
            description: 'Base64 encoded file content (for uploads)'
        }
    }
};
// ITutorialSectionTodo — inline (not exported from todoActions.openapi to avoid circular)
const TutorialSectionTodoSchema = {
    type: 'object',
    description: 'Association between a tutorial section and a todo (ITutorialSectionTodo)',
    properties: {
        id: { type: 'number', description: 'Association identifier' },
        todo: { ...todoActions_openapi_1.TodoSchema, description: 'The associated todo with its items (ITodo)' },
        created_at: { type: 'string', format: 'date-time' },
        updated_at: { type: 'string', format: 'date-time' }
    }
};
// ITutorialSection
const TutorialSectionSchema = {
    type: 'object',
    properties: {
        id: {
            type: 'number',
            description: 'Section identifier'
        },
        title: {
            type: 'string',
            description: 'Section title'
        },
        description: {
            type: 'string',
            description: 'Section description'
        },
        prompt: {
            type: 'string',
            description: 'AI prompt associated with the section'
        },
        order: {
            type: 'integer',
            description: 'Display order of the section'
        },
        screenFile: {
            ...FileSchema,
            description: 'Screenshot/image file for this section (ITutorialSection.screenFile)'
        },
        videoFile: {
            ...FileSchema,
            description: 'Video file for this section (ITutorialSection.videoFile)'
        },
        screenMiniFile: {
            ...FileSchema,
            description: 'Miniature screenshot file for this section (ITutorialSection.screenMiniFile)'
        },
        videoMiniFile: {
            ...FileSchema,
            description: 'Miniature video file for this section (ITutorialSection.videoMiniFile)'
        },
        knowledgeEntries: {
            type: 'array',
            description: 'Knowledge entries linked to this section (ITutorialSection.knowledgeEntries → IKnowledge[])',
            items: knowledgeActions_openapi_1.KnowledgeSchema
        },
        tutorialSectionTodos: {
            type: 'array',
            items: TutorialSectionTodoSchema,
            description: 'Todo associations for this section (ITutorialSection.tutorialSectionTodos)'
        },
        created_at: { type: 'string', format: 'date-time' },
        updated_at: { type: 'string', format: 'date-time' }
    },
    required: ['title']
};
// ITutorialStep
const TutorialStepSchema = {
    type: 'object',
    properties: {
        id: {
            type: 'number',
            description: 'Step identifier'
        },
        title: {
            type: 'string',
            description: 'Step title'
        },
        order: {
            type: 'integer',
            description: 'Display order of the step'
        },
        tutorialSections: {
            type: 'array',
            items: TutorialSectionSchema,
            description: 'Sections within this step (ITutorialStep.tutorialSections)'
        },
        created_at: { type: 'string', format: 'date-time' },
        updated_at: { type: 'string', format: 'date-time' }
    },
    required: ['title']
};
// ITutorial — shared for both list and get responses
exports.TutorialSchema = {
    type: 'object',
    properties: {
        id: {
            type: 'number',
            description: 'Tutorial unique identifier'
        },
        name: {
            type: 'string',
            description: 'Tutorial name',
            example: 'Getting Started with IntelliHub'
        },
        description: {
            type: 'string',
            description: 'Tutorial description'
        },
        introText: {
            type: 'string',
            description: 'Introductory text displayed before the tutorial begins'
        },
        tutorialGoal: {
            type: 'string',
            description: 'Goal the tutorial participant should achieve'
        },
        expireAt: {
            type: 'string',
            format: 'date-time',
            nullable: true,
            description: 'Date after which the tutorial is considered expired'
        },
        active: {
            type: 'boolean',
            description: 'Whether the tutorial is active/published'
        },
        owner: {
            type: 'string',
            description: 'ID of the owning user (parent user or super admin)'
        },
        creator: {
            type: 'object',
            description: 'User who created the tutorial',
            properties: {
                id: { type: 'number', description: 'User ID' },
                name: { type: 'string', description: 'User full name' },
                username: { type: 'string', description: 'Username' },
                email: { type: 'string', description: 'User email address' }
            }
        },
        avatar: {
            ...avatarActions_openapi_1.AvatarResponse,
            description: 'AI avatar associated with this tutorial'
        },
        company: {
            type: 'object',
            description: 'Company this tutorial belongs to',
            properties: {
                id: { type: 'number', description: 'Company ID' },
                name: { type: 'string', description: 'Company name' }
            }
        },
        logo: {
            ...FileSchema,
            description: 'Tutorial logo image'
        },
        instructionFile: {
            ...FileSchema,
            description: 'Instruction PDF or document'
        },
        tutorialSteps: {
            type: 'array',
            items: TutorialStepSchema,
            description: 'Ordered steps of the tutorial'
        },
        created_at: {
            type: 'string',
            format: 'date-time'
        },
        updated_at: {
            type: 'string',
            format: 'date-time'
        }
    }
};
const TutorialProgressPositionSchema = {
    type: 'object',
    properties: {
        id: { type: 'number' },
        title: { type: 'string' },
        description: { type: 'string' },
        order: { type: 'number' },
        number: { type: 'number', description: '1-based position (step in tutorial, section in its step)' }
    }
};
const ParticipantDataSchema = {
    type: 'object',
    properties: {
        id: {
            type: 'number',
            description: 'Participant unique identifier'
        },
        uuid: {
            type: 'string',
            description: 'Participant UUID used for progress tracking',
            example: '550e8400-e29b-41d4-a716-446655440000'
        },
        name: {
            type: 'string',
            description: 'Participant display name'
        },
        userIdentity: {
            type: 'boolean',
            description: 'This is true for inthub user participant which is auto created on tutorial entrance in the main application. It\'s false for API created participants which are not linked to any user and can be used for guest users or other identities'
        }
    }
};
const TutorialPayloadBody = {
    type: 'object',
    description: 'Tutorial creation/update payload',
    properties: {
        name: {
            type: 'string',
            description: 'Tutorial name',
            example: 'Getting Started with IntelliHub'
        },
        description: {
            type: 'string',
            description: 'Tutorial description'
        },
        introText: {
            type: 'string',
            description: 'Introductory text displayed before the tutorial begins'
        },
        tutorialGoal: {
            type: 'string',
            description: 'Goal the tutorial participant should achieve'
        },
        expireAt: {
            type: 'string',
            format: 'date-time',
            nullable: true,
            description: 'Date after which the tutorial is considered expired'
        },
        active: {
            type: 'boolean',
            description: 'Whether the tutorial is active/published',
            default: true
        },
        avatar: {
            type: 'object',
            description: 'Avatar to associate with this tutorial (IAvatar reference)',
            properties: {
                id: { type: 'number', description: 'Avatar ID' }
            },
            required: ['id']
        },
        logo: {
            ...FileSchema,
            description: 'Tutorial logo (new upload: provide filename/path/mimeType/size; existing: provide id)'
        },
        instructionFile: {
            ...FileSchema,
            description: 'Instruction file (new upload: provide filename/path/mimeType/size; existing: provide id)'
        },
        steps: {
            type: 'array',
            description: 'Ordered steps of the tutorial (ITutorialStepPayload[])',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'number', description: 'Existing step ID (omit for new steps)' },
                    title: { type: 'string', description: 'Step title' },
                    description: { type: 'string', description: 'Step description' },
                    order: { type: 'integer', description: 'Display order' },
                    sections: {
                        type: 'array',
                        description: 'Ordered sections (ITutorialSectionPayload[])',
                        items: {
                            type: 'object',
                            properties: {
                                id: { type: 'number', description: 'Existing section ID (omit for new sections)' },
                                title: { type: 'string', description: 'Section title' },
                                description: { type: 'string', description: 'Section description' },
                                prompt: { type: 'string', description: 'AI prompt for this section' },
                                order: { type: 'integer', description: 'Display order' },
                                knowledgeIds: {
                                    type: 'array',
                                    items: { type: 'string' },
                                    description: 'Knowledge entry IDs to link to this section'
                                },
                                todoItems: {
                                    type: 'array',
                                    description: 'Todo items for this section',
                                    items: {
                                        type: 'object',
                                        properties: {
                                            title: { type: 'string' },
                                            description: { type: 'string' },
                                            done: { type: 'boolean' },
                                            order: { type: 'integer' }
                                        },
                                        required: ['title']
                                    }
                                },
                                screenshot: {
                                    ...FileSchema,
                                    description: 'Screenshot/image file for this section (maps to screenFile)'
                                },
                                video: {
                                    ...FileSchema,
                                    description: 'Video file for this section (maps to videoFile)'
                                },
                                screenshotMini: {
                                    ...FileSchema,
                                    description: 'Miniature screenshot file for this section (maps to screenMiniFile)'
                                },
                                videoMini: {
                                    ...FileSchema,
                                    description: 'Miniature video file for this section (maps to videoMiniFile)'
                                }
                            },
                            required: ['title']
                        }
                    }
                },
                required: ['title']
            }
        }
    },
    required: ['name']
};
exports.tutorialOpenApiDocs = {
    'tutorial.list': {
        description: 'List tutorials with optional search query and pagination',
        payload: {
            params: {
                type: 'object',
                properties: {
                    q: {
                        type: 'string',
                        description: 'Optional search query to filter tutorials by name or description'
                    }
                }
            },
            query: {
                type: 'object',
                properties: {
                    p: {
                        type: 'integer',
                        description: 'Page number (1-indexed)',
                        minimum: 1,
                        default: 1
                    },
                    per_page: {
                        type: 'integer',
                        description: 'Number of items per page',
                        minimum: 1,
                        maximum: 100,
                        default: 20
                    },
                    companyId: {
                        type: 'string',
                        description: 'Filter tutorials by company ID'
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Paginated list of tutorials',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'array',
                            items: exports.TutorialSchema
                        },
                        count: {
                            type: 'integer',
                            description: 'Total number of tutorials matching the query'
                        }
                    }
                }
            },
            500: {
                description: 'Failed to fetch tutorials',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.get': {
        description: 'Get a single tutorial by ID with all steps, sections, and todo items.',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID',
                        required: true
                    }
                }
            },
            query: {
                type: 'object',
                properties: {
                    participantUuid: {
                        type: 'string',
                        description: 'Participant UUID to resolve per-participant todo done state'
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Tutorial details with all nested steps and sections',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.TutorialSchema
                    }
                }
            },
            404: {
                description: 'Tutorial not found or access denied',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to fetch tutorial',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.create': {
        description: 'Create a new tutorial with steps and sections',
        payload: {
            body: TutorialPayloadBody
        },
        responses: {
            200: {
                description: 'Created tutorial',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.TutorialSchema
                    }
                }
            },
            500: {
                description: 'Failed to create tutorial',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.update': {
        description: 'Update an existing tutorial, its steps, and sections. Steps/sections not included in the payload are deleted.',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID to update',
                        required: true
                    }
                }
            },
            body: TutorialPayloadBody
        },
        responses: {
            200: {
                description: 'Updated tutorial',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.TutorialSchema
                    }
                }
            },
            404: {
                description: 'Tutorial not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to update tutorial',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.delete': {
        description: 'Delete a tutorial and its associated files',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID to delete',
                        required: true
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Tutorial deleted successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true }
                    }
                }
            },
            404: {
                description: 'Tutorial not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to delete tutorial',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.participant.progress': {
        description: 'Get the current progress of a participant in a tutorial. If no participantUuid is provided, resolves or auto-creates the user identity participant.',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID',
                        required: true
                    }
                }
            },
            query: {
                type: 'object',
                properties: {
                    uuid: {
                        type: 'string',
                        description: 'Participant UUID (optional – defaults to current user identity participant)'
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Participant progress data',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'object',
                            properties: {
                                participant: ParticipantDataSchema,
                                section: {
                                    oneOf: [
                                        {
                                            type: 'object',
                                            properties: {
                                                id: { type: 'number' },
                                                title: { type: 'string' },
                                                description: { type: 'string' },
                                                order: { type: 'integer' }
                                            }
                                        },
                                        { type: 'null' }
                                    ],
                                    description: 'Current section of the participant (null if not started)'
                                },
                                step: {
                                    oneOf: [
                                        {
                                            type: 'object',
                                            properties: {
                                                id: { type: 'number' },
                                                title: { type: 'string' },
                                                description: { type: 'string' },
                                                order: { type: 'integer' }
                                            }
                                        },
                                        { type: 'null' }
                                    ],
                                    description: 'Current step of the participant (null if not started)'
                                }
                            }
                        }
                    }
                }
            },
            404: {
                description: 'Participant not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to fetch participant progress',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.participant.progress.save': {
        description: 'Save the current progress of a participant (which step and section they are on)',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID',
                        required: true
                    }
                }
            },
            body: {
                type: 'object',
                description: 'Progress save payload',
                properties: {
                    participantUuid: {
                        type: 'string',
                        description: 'UUID of the participant whose progress is being saved',
                        required: true
                    },
                    sectionId: {
                        type: 'string',
                        description: 'ID of the current tutorial section',
                        required: true
                    },
                    stepId: {
                        type: 'string',
                        description: 'ID of the current tutorial step',
                        required: true
                    }
                },
                required: ['participantUuid', 'sectionId', 'stepId']
            }
        },
        responses: {
            200: {
                description: 'Progress saved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true }
                    }
                }
            },
            404: {
                description: 'Participant, section, or step not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to save participant progress',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.participant.create': {
        description: 'Create a new (non-user-identity) participant for a tutorial',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID',
                        required: true
                    }
                }
            },
            body: {
                type: 'object',
                description: 'Participant creation payload',
                properties: {
                    name: {
                        type: 'string',
                        description: 'Participant display name',
                        required: true,
                        example: 'John Doe'
                    }
                },
                required: ['name']
            }
        },
        responses: {
            200: {
                description: 'Created participant',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: ParticipantDataSchema
                    }
                }
            },
            404: {
                description: 'Tutorial not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to create participant',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.participant.delete': {
        description: 'Delete a participant from a tutorial by their UUID',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID',
                        required: true
                    },
                    uuid: {
                        type: 'string',
                        description: 'Participant UUID to delete',
                        required: true
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Participant deleted successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true }
                    }
                }
            },
            404: {
                description: 'Participant not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to delete participant',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.participant.list': {
        description: 'List all participants of a tutorial',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID',
                        required: true
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'List of participants',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'array',
                            items: ParticipantDataSchema
                        }
                    }
                }
            },
            404: {
                description: 'Tutorial not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to list participants',
                returnParams: ErrorResponse
            }
        }
    },
    'tutorial.statistics.list': {
        description: 'List tutorial statistics: every participant of the tutorial with the saved step/section position and task completion counts',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Tutorial ID',
                        required: true
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Tutorial statistics',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'object',
                            properties: {
                                tutorialId: { type: 'number', description: 'Tutorial ID' },
                                tutorialName: { type: 'string', description: 'Tutorial name' },
                                totalTasks: { type: 'number', description: 'Number of tasks (todo items) in the whole tutorial' },
                                participants: {
                                    type: 'array',
                                    items: {
                                        type: 'object',
                                        properties: {
                                            ...ParticipantDataSchema.properties,
                                            step: {
                                                oneOf: [TutorialProgressPositionSchema, { type: 'null' }],
                                                description: 'Saved step of the participant'
                                            },
                                            section: {
                                                oneOf: [TutorialProgressPositionSchema, { type: 'null' }],
                                                description: 'Saved section of the participant'
                                            },
                                            completedTasks: { type: 'number', description: 'Tasks marked as done by the participant' },
                                            totalTasks: { type: 'number', description: 'Number of tasks in the tutorial' },
                                            percentage: {
                                                type: 'number',
                                                nullable: true,
                                                description: 'Completion percentage (0-100), null when the tutorial has no tasks'
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            404: {
                description: 'Tutorial not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to fetch tutorial statistics',
                returnParams: ErrorResponse
            }
        }
    }
};
